import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { ArrowLeft, Save, Search, X, PlusCircle, Trash2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const ITEM_VAZIO = {
  fe: '', descricao: '', medidas: '', unidade: 'Mil',
  qtd_min: '', preco_milhar: '', subtotal: 0, material_id: '',
};

const hoje = () => new Date().toISOString().split('T')[0];

const mascaraTel = (v) => {
  let n = v.replace(/\D/g, '').substring(0, 11);
  if (n.length <= 2) return n.length ? `(${n}` : '';
  if (n.length <= 6) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
  if (n.length <= 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
  return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
};

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const inp = "w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all";
const lbl = "text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block";
const inpTable = "w-full bg-transparent border border-transparent rounded-lg px-1.5 py-1 text-xs outline-none focus:bg-white focus:border-[#0A3D73]/30 transition-all";

const makeInitial = () => ({
  numero_proposta: '', data_emissao: hoje(), validade: '20 DIAS',
  representada_id: '', representada_nome: '',
  cliente_id: '', cliente_nome: '', cliente_cnpj: '',
  cliente_endereco: '', cliente_contato: '', cliente_telefone: '',
  itens: [{ ...ITEM_VAZIO }],
  prazo_pagamento: '60 dias', frete: 'CIF',
  prazo_entrega: '15 dias após confirmação',
  icms: '20%', ipi: 15,
  subtotal: 0, valor_ipi: 0, valor_total: 0,
  status: 'PENDENTE', observacoes: '',
});

export default function OrcamentoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [form, setForm] = useState(makeInitial);
  const [initialData, setInitialData] = useState(makeInitial);
  const [clientes, setClientes] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [representadas, setRepresentadas] = useState([]);
  const [condicoes, setCondicoes] = useState([]);
  const [prazoOutra, setPrazoOutra] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const [clienteBusca, setClienteBusca] = useState('');
  const [clienteSugestoes, setClienteSugestoes] = useState([]);
  const [representadaBusca, setRepresentadaBusca] = useState('');
  const [representadaSugestoes, setRepresentadaSugestoes] = useState([]);
  const [feDropdownIdx, setFeDropdownIdx] = useState(null);
  const [feSugestoes, setFeSugestoes] = useState([]);

  useEffect(() => {
    const init = async () => {
      try {
        const [cRes, mRes, rRes, cpRes] = await Promise.all([
          api.get('/clientes').catch(() => ({ data: [] })),
          api.get('/materiais').catch(() => ({ data: [] })),
          api.get('/representadas').catch(() => ({ data: [] })),
          api.get('/condicoes-pagamento').catch(() => ({ data: [] })),
        ]);
        setClientes(Array.isArray(cRes.data) ? cRes.data : []);
        setMateriais(Array.isArray(mRes.data) ? mRes.data : []);
        setRepresentadas(Array.isArray(rRes.data) ? rRes.data : []);
        const condicoesList = Array.isArray(cpRes.data) ? cpRes.data : [];
        setCondicoes(condicoesList);

        if (isEditing) {
          const { data: orc } = await api.get(`/orcamentos/${id}`);
          const populated = {
            ...makeInitial(),
            ...orc,
            itens: orc.itens?.length
              ? orc.itens.map(it => ({ ...ITEM_VAZIO, ...it }))
              : [{ ...ITEM_VAZIO }],
          };
          setForm(populated);
          setInitialData(populated);
          setClienteBusca(orc.cliente_nome || '');
          setRepresentadaBusca(orc.representada_nome || '');
          if (orc.prazo_pagamento && !condicoesList.some(c => c.descricao === orc.prazo_pagamento)) {
            setPrazoOutra(true);
          }
        }
      } catch {
        toast.error('Erro ao carregar dados');
        navigate('/orcamentos');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!clienteBusca.trim()) { setClienteSugestoes([]); return; }
    const q = clienteBusca.toLowerCase();
    setClienteSugestoes(
      clientes.filter(c => c.nome?.toLowerCase().includes(q) || c.cnpj?.includes(q)).slice(0, 6)
    );
  }, [clienteBusca, clientes]);

  useEffect(() => {
    if (!representadaBusca.trim()) { setRepresentadaSugestoes([]); return; }
    const q = representadaBusca.toLowerCase();
    setRepresentadaSugestoes(
      representadas.filter(r =>
        r.nome?.toLowerCase().includes(q) ||
        (r.nome_fantasia || '').toLowerCase().includes(q)
      ).slice(0, 6)
    );
  }, [representadaBusca, representadas]);

  const selecionarCliente = (c) => {
    setForm(f => ({
      ...f,
      cliente_id: c.id,
      cliente_nome: c.nome,
      cliente_cnpj: c.cnpj || '',
      cliente_endereco: c.endereco || '',
      cliente_contato: c.comprador || '',
      cliente_telefone: c.telefone || '',
    }));
    setClienteBusca(c.nome);
    setClienteSugestoes([]);
  };

  const selecionarRepresentada = (r) => {
    setForm(f => ({ ...f, representada_id: r.id, representada_nome: r.nome_fantasia || r.nome }));
    setRepresentadaBusca(r.nome_fantasia || r.nome);
    setRepresentadaSugestoes([]);
  };

  const recalcular = (itens, ipi) => {
    const sub = itens.reduce((a, it) => a + (Number(it.subtotal) || 0), 0);
    const vipi = sub * (Number(ipi) || 0) / 100;
    return { subtotal: sub, valor_ipi: vipi, valor_total: sub + vipi };
  };

  const atualizarItem = (idx, field, val) => {
    setForm(f => {
      const itens = [...f.itens];
      itens[idx] = { ...itens[idx], [field]: val };
      const q = Number(itens[idx].qtd_min) || 0;
      const p = Number(itens[idx].preco_milhar) || 0;
      itens[idx].subtotal = q * p;
      return { ...f, itens, ...recalcular(itens, f.ipi) };
    });
  };

  const handleFeItemChange = (idx, val) => {
    atualizarItem(idx, 'fe', val);
    if (val.trim().length >= 1) {
      const q = val.toLowerCase();
      const sugs = materiais.filter(m =>
        (m.numero_fe || '').toLowerCase().includes(q) ||
        (m.descricao || '').toLowerCase().includes(q) ||
        (m.nome || '').toLowerCase().includes(q)
      ).slice(0, 6);
      setFeSugestoes(sugs);
      setFeDropdownIdx(sugs.length > 0 ? idx : null);
    } else {
      setFeSugestoes([]);
      setFeDropdownIdx(null);
    }
  };

  const selecionarMaterial = (idx, mat) => {
    setForm(f => {
      const itens = [...f.itens];
      const medidas = [mat.comprimento, mat.largura, mat.altura].filter(v => v && v > 0).join(' x ');
      const newPreco = Number(mat.preco_unit) || 0;
      itens[idx] = {
        ...itens[idx],
        fe: mat.numero_fe || '',
        descricao: mat.descricao || mat.nome || '',
        medidas: medidas || itens[idx].medidas,
        preco_milhar: newPreco,
        material_id: mat.id || '',
      };
      const q = Number(itens[idx].qtd_min) || 0;
      itens[idx].subtotal = q * newPreco;
      return { ...f, itens, ...recalcular(itens, f.ipi) };
    });
    setFeDropdownIdx(null);
    setFeSugestoes([]);
  };

  const addItem = () => setForm(f => ({ ...f, itens: [...f.itens, { ...ITEM_VAZIO }] }));

  const removeItem = (idx) => setForm(f => {
    const itens = f.itens.filter((_, i) => i !== idx);
    return { ...f, itens, ...recalcular(itens, f.ipi) };
  });

  const setIpi = (val) => setForm(f => ({ ...f, ipi: val, ...recalcular(f.itens, val) }));

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialData);

  const handleBack = () => {
    if (isDirty && !window.confirm('Há alterações não salvas. Deseja sair mesmo assim?')) return;
    navigate('/orcamentos');
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    const itemInvalido = form.itens.find(it => !(Number(it.qtd_min) > 0 && Number(it.preco_milhar) > 0));
    if (itemInvalido) {
      toast.error('Há item com quantidade ou preço zerado. Corrija ou remova.');
      return;
    }
    if (Number(form.valor_total) <= 0) {
      toast.error('O valor total do orçamento deve ser maior que zero');
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/orcamentos/${id}`, form);
        toast.success('Orçamento atualizado!');
      } else {
        await api.post('/orcamentos', form);
        toast.success('Orçamento criado!');
      }
      navigate('/orcamentos');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Orçamento...</p>
    </div>
  );

  return (
    <form id="orcamento-form" onSubmit={handleSalvar} className="min-h-screen bg-[#E9EEF2]">

      {/* HEADER */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button type="button" onClick={handleBack}
              className="flex items-center gap-1.5 text-xs font-black uppercase text-slate-500 hover:text-[#0A3D73] transition-colors">
              <ArrowLeft size={14} /> Voltar
            </button>
            <div className="w-px h-5 bg-slate-200" />
            <div>
              <h1 className="text-base font-black text-slate-900">
                {isEditing
                  ? `Editar Orçamento — ${form.numero_proposta || id}`
                  : 'Novo Orçamento'}
              </h1>
              <p className="text-[11px] text-slate-400 font-semibold">Orçamentos</p>
            </div>
          </div>
          <button
            type="submit"
            form="orcamento-form"
            disabled={saving}
            className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-5 py-2.5 rounded-xl shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Save size={14} /> {saving ? 'Salvando...' : 'Salvar Orçamento'}
          </button>
        </div>
      </div>

      {/* BODY */}
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6 max-w-7xl mx-auto">

          {/* ── COL ESQUERDA ── */}
          <div className="space-y-6">

            {/* Card 01 · Dados da Proposta */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">01 · Dados da Proposta</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={lbl}>Nº Proposta</label>
                  <input
                    type="text"
                    className={inp}
                    placeholder="PROP-..."
                    value={form.numero_proposta}
                    onChange={e => setForm(f => ({ ...f, numero_proposta: e.target.value }))} />
                </div>
                <div>
                  <label className={lbl}>Data Emissão</label>
                  <input
                    type="date"
                    className={inp}
                    value={form.data_emissao}
                    onChange={e => setForm(f => ({ ...f, data_emissao: e.target.value }))} />
                </div>
                <div>
                  <label className={lbl}>Validade</label>
                  <input
                    type="text"
                    className={inp}
                    placeholder="20 DIAS"
                    value={form.validade}
                    onChange={e => setForm(f => ({ ...f, validade: e.target.value }))} />
                </div>
              </div>
              <div className="relative mt-4">
                <label className={lbl}>Representada</label>
                <div className="flex items-center gap-2 border border-slate-200 rounded-lg bg-white px-3 py-2">
                  <Search size={13} className="text-slate-400 flex-shrink-0" />
                  <input
                    type="text"
                    className="flex-1 bg-transparent text-sm outline-none text-slate-800 font-semibold"
                    placeholder="Digite o nome da representada..."
                    value={representadaBusca}
                    onChange={e => setRepresentadaBusca(e.target.value)} />
                  {form.representada_nome && (
                    <button type="button" onClick={() => {
                      setRepresentadaBusca('');
                      setForm(f => ({ ...f, representada_id: '', representada_nome: '' }));
                    }} className="text-slate-300 hover:text-red-400 transition-colors">
                      <X size={12} />
                    </button>
                  )}
                </div>
                {representadaSugestoes.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-lg z-20 mt-1 max-h-48 overflow-auto">
                    {representadaSugestoes.map(r => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => selecionarRepresentada(r)}
                        className="w-full text-left px-4 py-2.5 text-xs hover:bg-blue-50 border-b border-slate-50 last:border-0 transition-colors">
                        <span className="font-black text-slate-800">{r.nome_fantasia || r.nome}</span>
                        {r.nome_fantasia && <span className="text-slate-400 ml-2">{r.nome}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Card 02 · Dados do Cliente */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">02 · Dados do Cliente</p>
              <div className="relative mb-4">
                <label className={lbl}>Buscar Cliente</label>
                <div className="flex items-center gap-2 border border-slate-200 rounded-lg bg-white px-3 py-2">
                  <Search size={13} className="text-slate-400 flex-shrink-0" />
                  <input
                    type="text"
                    className="flex-1 bg-transparent text-sm outline-none text-slate-800 font-semibold"
                    placeholder="Digite o nome ou CNPJ..."
                    value={clienteBusca}
                    onChange={e => setClienteBusca(e.target.value)} />
                </div>
                {clienteSugestoes.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-lg z-20 mt-1 max-h-48 overflow-auto">
                    {clienteSugestoes.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => selecionarCliente(c)}
                        className="w-full text-left px-4 py-2.5 text-xs hover:bg-blue-50 border-b border-slate-50 last:border-0 transition-colors">
                        <span className="font-black text-slate-800">{c.nome}</span>
                        <span className="text-slate-400 ml-2">{c.cnpj}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Razão Social</label>
                  <input type="text" className={inp} value={form.cliente_nome}
                    onChange={e => setForm(f => ({ ...f, cliente_nome: e.target.value }))} />
                </div>
                <div>
                  <label className={lbl}>CNPJ</label>
                  <input type="text" className={inp} value={form.cliente_cnpj}
                    onChange={e => setForm(f => ({ ...f, cliente_cnpj: e.target.value }))} />
                </div>
                <div className="col-span-1 md:col-span-2">
                  <label className={lbl}>Endereço</label>
                  <input type="text" className={inp} value={form.cliente_endereco}
                    onChange={e => setForm(f => ({ ...f, cliente_endereco: e.target.value }))} />
                </div>
                <div>
                  <label className={lbl}>Contato</label>
                  <input type="text" className={inp} value={form.cliente_contato}
                    onChange={e => setForm(f => ({ ...f, cliente_contato: e.target.value }))} />
                </div>
                <div>
                  <label className={lbl}>Telefone</label>
                  <input
                    type="text"
                    className={inp}
                    value={form.cliente_telefone}
                    maxLength={15}
                    onChange={e => setForm(f => ({ ...f, cliente_telefone: mascaraTel(e.target.value) }))} />
                </div>
              </div>
            </div>

            {/* Card 03 · Itens do Orçamento */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">03 · Itens do Orçamento</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[700px]">
                  <thead>
                    <tr className="bg-[#0A3D73] text-white">
                      {['Nº', 'FE', 'Descrição', 'Medidas', 'Unid.', 'Qtd. Mín.', 'Preço/Milh.', 'Subtotal', ''].map((h, i) => (
                        <th key={i} className="px-2 py-2.5 text-center font-bold tracking-wide" style={{ fontSize: 9, textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.itens.map((it, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td className="px-1 py-1 text-center font-black text-[#0A3D73] text-xs w-8">{idx + 1}</td>
                        <td className="px-1 py-1 w-24 relative">
                          <input
                            type="text"
                            className={inpTable}
                            placeholder="FE código"
                            value={it.fe}
                            onChange={e => handleFeItemChange(idx, e.target.value)}
                            onBlur={() => setTimeout(() => setFeDropdownIdx(null), 150)} />
                          {feDropdownIdx === idx && feSugestoes.length > 0 && (
                            <div className="absolute top-full left-0 bg-white border border-slate-200 rounded-lg shadow-lg z-30 w-52 max-h-40 overflow-auto">
                              {feSugestoes.map(mat => (
                                <button
                                  key={mat.id}
                                  type="button"
                                  onMouseDown={e => e.preventDefault()}
                                  onClick={() => selecionarMaterial(idx, mat)}
                                  className="w-full text-left px-2 py-2 text-xs hover:bg-blue-50 border-b border-slate-50 last:border-0">
                                  <span className="font-black text-slate-800 block">{mat.numero_fe}</span>
                                  <span className="text-slate-400 block truncate">{mat.descricao || mat.nome}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </td>
                        {[['descricao', 'Descrição', 'w-32'], ['medidas', '350 x 250 x 210', 'w-32'], ['unidade', 'Mil', 'w-12']].map(([k, ph, w]) => (
                          <td key={k} className={`px-1 py-1 ${w}`}>
                            <input
                              type="text"
                              className={inpTable}
                              placeholder={ph}
                              value={it[k]}
                              onChange={e => atualizarItem(idx, k, e.target.value)} />
                          </td>
                        ))}
                        <td className="px-1 py-1 w-16">
                          <input
                            type="number"
                            className={`${inpTable} text-center`}
                            placeholder="0"
                            value={it.qtd_min}
                            onChange={e => atualizarItem(idx, 'qtd_min', e.target.value)} />
                        </td>
                        <td className="px-1 py-1 w-24">
                          <input
                            type="number"
                            className={`${inpTable} text-right`}
                            placeholder="0,00"
                            step="0.01"
                            value={it.preco_milhar}
                            onChange={e => atualizarItem(idx, 'preco_milhar', e.target.value)} />
                        </td>
                        <td className="px-2 py-1 text-right font-black text-[#0A3D73] text-xs whitespace-nowrap">
                          {it.subtotal > 0 ? brl(it.subtotal) : '—'}
                        </td>
                        <td className="px-1 py-1 text-center w-7">
                          {form.itens.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItem(idx)}
                              className="text-slate-300 hover:text-red-500 transition-colors">
                              <Trash2 size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                onClick={addItem}
                className="mt-3 flex items-center gap-2 text-xs font-bold text-[#0A3D73] border border-dashed border-[#0A3D73]/30 rounded-lg px-4 py-2 hover:bg-blue-50 transition-colors">
                <PlusCircle size={14} /> Adicionar Item
              </button>
              <div className="mt-4 flex justify-end">
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 w-64 flex flex-col gap-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Subtotal</span>
                    <strong className="text-slate-800">{brl(form.subtotal)}</strong>
                  </div>
                  <div className="flex justify-between text-sm items-center gap-2">
                    <span className="text-slate-500 whitespace-nowrap">IPI</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        className="w-14 text-center text-xs border border-slate-200 rounded-lg px-1 py-0.5 outline-none focus:border-[#0A3D73]"
                        value={form.ipi}
                        step="0.01"
                        onChange={e => setIpi(e.target.value)} />
                      <span className="text-xs text-slate-400">%</span>
                    </div>
                    <strong className="text-slate-800">{brl(form.valor_ipi)}</strong>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t-2 border-[#0A3D73]/20">
                    <strong className="text-[#0A3D73] text-sm">Valor Total</strong>
                    <strong className="text-[#0A3D73] text-lg">{brl(form.valor_total)}</strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 04 · Condições Comerciais */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">04 · Condições Comerciais</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className={lbl}>Prazo Pagamento</label>
                  <select
                    value={prazoOutra ? '__outra__' : form.prazo_pagamento}
                    onChange={e => {
                      if (e.target.value === '__outra__') {
                        setPrazoOutra(true);
                        setForm(f => ({ ...f, prazo_pagamento: '' }));
                      } else {
                        setPrazoOutra(false);
                        setForm(f => ({ ...f, prazo_pagamento: e.target.value }));
                      }
                    }}
                    className={`${inp} cursor-pointer`}>
                    <option value="">— Selecione —</option>
                    {condicoes.map(c => <option key={c.id} value={c.descricao}>{c.descricao}</option>)}
                    <option value="__outra__">Outra...</option>
                  </select>
                  {prazoOutra && (
                    <input
                      type="text"
                      className={`${inp} mt-1`}
                      placeholder="Digite a condição..."
                      value={form.prazo_pagamento}
                      onChange={e => setForm(f => ({ ...f, prazo_pagamento: e.target.value }))} />
                  )}
                </div>
                {[['Frete', 'frete'], ['Prazo Entrega', 'prazo_entrega'], ['ICMS', 'icms']].map(([l, k]) => (
                  <div key={k}>
                    <label className={lbl}>{l}</label>
                    <input type="text" className={inp} value={form[k]}
                      onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
                  </div>
                ))}
                <div className="col-span-2 md:col-span-3">
                  <label className={lbl}>Observações</label>
                  <input type="text" className={inp} value={form.observacoes}
                    onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} />
                </div>
              </div>
            </div>

          </div>

          {/* ── COL DIREITA ── */}
          <div>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 lg:sticky lg:top-[73px]">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Resumo da Proposta</p>
              <div className="space-y-4">

                {/* Número */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Nº Proposta</span>
                  <span className="text-sm font-black font-mono text-[#0A3D73]">{form.numero_proposta || '—'}</span>
                </div>

                {/* Cliente */}
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Cliente</p>
                  <p className="text-sm font-black text-slate-800 truncate">{form.cliente_nome || '—'}</p>
                  {form.cliente_cnpj && (
                    <p className="text-[11px] text-slate-400 font-mono mt-0.5">{form.cliente_cnpj}</p>
                  )}
                </div>

                {/* Representada */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Representada</span>
                  <span className="text-xs font-bold text-slate-600 truncate max-w-[60%] text-right">{form.representada_nome || '—'}</span>
                </div>

                {/* Itens */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Itens</span>
                  <span className="text-xs font-black text-slate-700">
                    {form.itens.length} item{form.itens.length !== 1 ? 'ns' : ''}
                  </span>
                </div>

                {/* Subtotal + IPI */}
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Subtotal</span>
                    <strong className="text-slate-700">{brl(form.subtotal)}</strong>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">IPI ({form.ipi}%)</span>
                    <strong className="text-slate-700">{brl(form.valor_ipi)}</strong>
                  </div>
                </div>

                {/* Total Geral */}
                <div className="bg-[#DCFCE7] rounded-xl p-4 text-center border border-emerald-200">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700/70 mb-1 flex items-center justify-center gap-1">
                    <DollarSign size={10} /> Total Geral
                  </p>
                  <p className="text-2xl font-black text-emerald-800">{brl(form.valor_total)}</p>
                </div>

                {/* Validade + Prazo */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Validade</span>
                    <span className="text-xs font-bold text-slate-600">{form.validade || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Prazo Pgto.</span>
                    <span className="text-xs font-bold text-slate-600">{form.prazo_pagamento || '—'}</span>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </form>
  );
}
